import { getCurrentEnterpriseId } from '@mews-commander/core';
import { GeneralEnterpriseSubscriptionSupportTier, GeneralSubscription } from '@mews-commander/data-access';
import { isCurrentProfilePlatformAdmin } from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { getCurrentEnterpriseSubscription } from '@mews-commander/legacy-entities/src/selectors/session/subscription';
import { CommanderThunkAction } from '@mews-commander/types';
import { createPrefixer, secureIframe } from '@mews-commander/utils';
import { createAction, loadScript, retryPromise } from '@mews/framework';

import { prefix } from './module';
import { getUserSalesForceConfiguration } from './selectors';
import { getSalesForceChatCloseButton, getSalesForceChatOpenButton } from './utils';

const IFRAME_SELECTORS = {
    salesForceFileUpload: '#fileUploadIframe',
    salesForce: '#esw_storage_iframe',
} as const;

const DIALOG_MODULE_NAME = 'dialog';
const dialogPrefix = createPrefixer(DIALOG_MODULE_NAME);

// duplicated actions to isolate chat into its own module
// mews-js\packages\commander\modules\modules\src\modules\dialog\reducer.ts
const OPEN_DIALOG = dialogPrefix('OPEN_DIALOG');
const CLOSE_DIALOG = dialogPrefix('CLOSE_DIALOG');

// duplicated const to isolate chat into its own module
// HELP_CENTER_SIDEBAR_NAME from mews-js\packages\commander\modules\modules\src\modules\helpCenter\module.ts
const HELP_CENTER_SIDEBAR_NAME = 'helpCenterSidebar';

const openDialog = createAction(OPEN_DIALOG, HELP_CENTER_SIDEBAR_NAME);
const closeDialog = createAction(CLOSE_DIALOG, HELP_CENTER_SIDEBAR_NAME);

const CHAT_LOADED = prefix('CHAT_LOADED');
export const SALES_FORCE_CHAT_LOADED = prefix('SALES_FORCE_CHAT_LOADED');
export const SALES_FORCE_CHAT_INITIALIZED = prefix('SALES_FORCE_CHAT_INITIALIZED');

const SALES_FORCE_SERVICE_SCRIPT = 'https://service.force.com/embeddedservice/5.0/esw.min.js';
const SALES_FORCE_SCRIPT = 'https://mews.my.salesforce.com/embeddedservice/5.0/esw.min.js';
const SALES_FORCE_BASE_URL = 'https://service.force.com';

export const loadSalesForce =
    (): CommanderThunkAction =>
    (dispatch, getState, { loggerService }) => {
        const isPlatformAdmin = isCurrentProfilePlatformAdmin();
        const enterpriseId = getCurrentEnterpriseId();

        const shouldLoadSalesForce = !(window as $TSFixMe).embedded_svc && !isPlatformAdmin;

        if (shouldLoadSalesForce) {
            const promise = loadScript(SALES_FORCE_SERVICE_SCRIPT)
                .then(() => dispatch(createAction(SALES_FORCE_CHAT_LOADED)))
                .catch((error) => {
                    loggerService.log('script-load-error', 'SalesForce javascript could not be loaded.', {
                        error,
                        enterpriseId,
                    });
                });

            void dispatch(createAction(CHAT_LOADED, promise));
        }
    };

const SALES_FORCE_INIT_PARAMETERS = {
    salesforceURL: 'https://mews.my.salesforce.com',
    communityURL: 'https://help.mews.com',
    org: '00D09000001JyzF',
    snapInName: 'Chat',
    baseLiveAgentContentURL: 'https://c.la3-c2-fra.salesforceliveagent.com/content',
    deploymentId: '57209000000UJnS',
    buttonId: '57309000000ULH4',
    baseLiveAgentURL: 'https://d.la3-c2-fra.salesforceliveagent.com/chat',
    eswLiveAgentDevName: 'EmbeddedServiceLiveAgent_Parent04I09000000PxigEAC_17b543fabff',
    isOfflineSupportEnabled: false,
};

export const initSalesForce =
    (): CommanderThunkAction =>
    async (dispatch, getState, { loggerService }) => {
        const state = getState();
        const config = getUserSalesForceConfiguration(state);
        const { extraPrechatFormDetails, extraPrechatInfo, prepopulatedPrechatFields } = config;

        const initESW = (gslbBaseURL: $TSFixMe) => {
            (window as $TSFixMe).embedded_svc.settings = {
                ...(window as $TSFixMe).embedded_svc.settings,
                displayHelpButton: false,
                language: 'en-US',
                enabledFeatures: ['LiveAgent'],
                entryFeature: 'LiveAgent',
                extraPrechatFormDetails,
                extraPrechatInfo,
                prepopulatedPrechatFields,
            };

            const {
                salesforceURL,
                communityURL,
                org,
                snapInName,
                baseLiveAgentContentURL,
                deploymentId,
                buttonId,
                baseLiveAgentURL,
                eswLiveAgentDevName,
                isOfflineSupportEnabled,
            } = SALES_FORCE_INIT_PARAMETERS;

            (window as $TSFixMe).embedded_svc.init(salesforceURL, communityURL, gslbBaseURL, org, snapInName, {
                baseLiveAgentContentURL,
                deploymentId,
                buttonId,
                baseLiveAgentURL,
                eswLiveAgentDevName,
                isOfflineSupportEnabled,
            });

            dispatch(createAction(SALES_FORCE_CHAT_INITIALIZED));

            Promise.all([
                retryPromise(async () => secureIframe(IFRAME_SELECTORS.salesForce)),
                retryPromise(async () => secureIframe(IFRAME_SELECTORS.salesForceFileUpload)),
            ]).catch((error) => {
                loggerService.log('iframe-error', `Iframe ${error} could not be found`, {
                    error,
                    enterpriseId: getCurrentEnterpriseId(),
                });
            });
        };

        if (!(window as $TSFixMe).embedded_svc) {
            return loadScript(SALES_FORCE_SCRIPT).then(() => {
                initESW(null);
            });
        }

        initESW(SALES_FORCE_BASE_URL);
        return Promise.resolve();
    };

export const open = (): CommanderThunkAction => (dispatch, getState) => {
    const { supportTier }: GeneralSubscription = getCurrentEnterpriseSubscription(getState());

    if (supportTier === GeneralEnterpriseSubscriptionSupportTier.Standard) {
        dispatch(openDialog);
        return;
    }

    const salesForceChatOpenButton = getSalesForceChatOpenButton();

    if (salesForceChatOpenButton) {
        (salesForceChatOpenButton as $TSFixMe).click();
    }
};

export const close = (): CommanderThunkAction => (dispatch, getState) => {
    const { supportTier }: GeneralSubscription = getCurrentEnterpriseSubscription(getState());

    if (supportTier === GeneralEnterpriseSubscriptionSupportTier.Standard) {
        dispatch(closeDialog);
        return;
    }

    const salesForceChatCloseButton = getSalesForceChatCloseButton();

    if (salesForceChatCloseButton) {
        (salesForceChatCloseButton as $TSFixMe).click();
    }
};
